/**
 * App-wide error surfaces (friendly, never a blank screen).
 *
 * - `ErrorScreen`  — presentational full-page friendly error / 404.
 * - `RouteError`   — React Router `errorElement`: catches loader/lazy/render
 *   errors in a route subtree, reports them, and renders `ErrorScreen`
 *   (a 404 route response gets the "page introuvable" variant).
 * - `TopErrorBoundary` — top-level class boundary wrapping the whole app so a
 *   render error ABOVE the shell (or on a public route) is caught + reported.
 *
 * Complements the in-shell `RouteErrorBoundary` (which keeps the sidebar alive
 * for a page-level crash); this covers everything above/around it.
 */
import { Component, type ErrorInfo, type ReactNode, useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { reportClientError } from '@/lib/errorReporting';

function ErrorScreen({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex min-h-screen w-full flex-col items-center justify-center gap-5 bg-bg p-6 text-center text-foreground"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
      </span>
      <div className="max-w-md">
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted">
          Notre équipe a été prévenue automatiquement. Vous pouvez réessayer ou revenir à l’accueil.
        </p>
        {detail && <p className="mt-1 text-xs text-muted">{detail}</p>}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={onRetry ?? (() => window.location.reload())}
          className="rounded-token bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:brightness-110"
        >
          Réessayer
        </button>
        <a
          href="/"
          className="rounded-token border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-surface-2"
        >
          Accueil
        </a>
      </div>
    </div>
  );
}

export function RouteError() {
  const error = useRouteError();

  useEffect(() => {
    if (isRouteErrorResponse(error)) {
      if (error.status >= 500) {
        reportClientError({
          message: `Route ${error.status}: ${error.statusText}`,
          source: 'route',
        });
      }
    } else if (error) {
      const e = error as Error;
      reportClientError({ message: e.message || 'Route error', stack: e.stack, source: 'route' });
    }
  }, [error]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <ErrorScreen title="Page introuvable" detail="Cette page n’existe pas ou a été déplacée." />;
  }
  return <ErrorScreen title="Une erreur est survenue" />;
}

interface BoundaryState {
  error: Error | null;
}

export class TopErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      source: 'render',
    });
  }

  render(): ReactNode {
    if (this.state.error) {
      return <ErrorScreen title="Une erreur est survenue" onRetry={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}
